/**
 * Dark-mode context: persisted in localStorage, defaults to the OS-level
 * ``prefers-color-scheme``. The top bar dark-mode toggle flips the value;
 * AntD's ConfigProvider re-renders with the appropriate algorithm.
 *
 * The hook lives in ``./useDarkMode.ts`` so this file only exports a
 * component (keeps the react-refresh ESLint rule happy).
 */

import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { DarkModeContext, type DarkMode } from "./darkModeContext";

const STORAGE_KEY = "sa.theme";

function readInitialMode(): DarkMode {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

export const DarkModeProvider = ({ children }: PropsWithChildren) => {
  const [mode, setModeState] = useState<DarkMode>(() => readInitialMode());

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, mode);
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  const toggle = useCallback(() => {
    setModeState((m) => (m === "dark" ? "light" : "dark"));
  }, []);

  const setMode = useCallback((m: DarkMode) => setModeState(m), []);

  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>;
};
